import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { Box } from 'rebass/styled-components';
import { Button, Loader } from '@getro/rombo';
import { Plus } from 'lucide-react';
import usePageTitle from '../../hooks/usePageTitle';
import MainLayout from '../../components/layout/Main';
import { MainLayoutHeader } from '../../components/atoms/mainLayoutHeader';
import DataTable from '../../components/molecules/agGrid';
import { AgentTitle } from './components/agentTitle';
import { useCampaignsTableRows, getCampaignsColumns } from './hooks/useCampaignsTableRows';
import { CampaignStatusRenderer, CampaignRenderer } from './renderers';
import { useGetSourcingCampaignsQuery } from '../../services/sourcing';
import { CreateCampaignModal } from './components/createCampaignModal';
import SourcingAgent from './sourcingAgent';
import { userSelector } from '../../redux/selectors';

const SourcingCampaigns = ({ userEmail }) => {
  usePageTitle('Sourcing Agent - Campaigns');

  const [isModalOpen, setIsModalOpen] = useState(false);

  const { data: campaigns = [], isLoading, isFetching } = useGetSourcingCampaignsQuery();

  const campaignsColumns = useMemo(() => getCampaignsColumns({ campaignCount: campaigns.length }), [campaigns.length]);
  const tableRows = useCampaignsTableRows({ campaigns });
  const user = useSelector(userSelector);

  if (isLoading) {
    return (
      <MainLayout>
        <Box p="24px" display="flex" justifyContent="center" alignItems="center" height="calc(100vh - 40px)">
          <Loader />
        </Box>
      </MainLayout>
    );
  }

  if (!campaigns.length) {
    return <SourcingAgent userEmail={userEmail} />;
  }

  return (
    <MainLayout>
      <MainLayoutHeader
        title={<AgentTitle />}
        actions={[
          <Button variant="accent" size="small" icon={Plus} iconGap="8px" onClick={() => setIsModalOpen(true)}>
            New campaign
          </Button>,
        ]}
      />

      <Box
        sx={{
          borderTop: '1px solid',
          borderColor: 'border.subtle',
          mt: '20px',
        }}
      >
        <DataTable
          showActions={false}
          useHorizontalActions={false}
          fixedColumn="first"
          items={tableRows}
          columnDefinition={campaignsColumns}
          hasMore={false}
          isInitialized={!isLoading}
          isLoadingMore={isFetching}
          persistColumnKey="sourcing-campaigns-v5"
          gridOptions={{
            suppressRowClickSelection: false,
            cellSelection: true,
            animateRows: false,
          }}
          rowActions={{
            height: 180,
          }}
          components={{
            campaignStatusRenderer: CampaignStatusRenderer,
            campaignRenderer: CampaignRenderer,
          }}
          sx={{
            height: 'calc(100vh - 90px)!important',
          }}
          cellStyle={{
            padding: '12px 8px',
          }}
        />
      </Box>

      {isModalOpen && <CreateCampaignModal onClose={() => setIsModalOpen(false)} userEmail={user.email} />}
    </MainLayout>
  );
};

SourcingCampaigns.propTypes = {
  userEmail: PropTypes.string.isRequired,
};

export default SourcingCampaigns;
